<script lang="ts" setup>
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextBody1 from '../typography/body/PTextBody1.vue'
import { PMaxLines } from '../typography/types'
import PCard from '../card/PCard.vue'
import { PCardRound } from '../card/types'
import { PSelectCardVariant } from './types'

type Props = {
  label: string
  variant?: PSelectCardVariant
  disable?: boolean
  cy: string
}

const modelValue = defineModel<boolean>({ default: false })

const props = withDefaults(defineProps<Props>(), {
  variant: PSelectCardVariant.WHITE,
  disable: false,
})

type Emits = {
  (e: 'click', evt: Event): void
}

const emits = defineEmits<Emits>()

const cardClass = computed(() => {
  return {
    'ds-select-card__active': modelValue.value,
    'ds-select-card__gray': props.variant === PSelectCardVariant.GRAY,
    'ds-select-card__disable': props.disable,
  }
})

const handleClick = (e: Event) => {
  if (!props.disable) {
    modelValue.value = !modelValue.value
    emits('click', e)
  }
}
</script>

<template>
  <div
    class="ds-select-card p-flex p-items-center p-no-wrap"
    :class="[cardClass]"
    :data-cy="cy"
    @click="(e) => handleClick(e)"
  >
    <slot name="default" />
    <p-space v-if="$slots['default']" :mobile="PSpaceSize.SUB_HEADLINE" />

    <p-text-body1 :text="label" :max-lines="PMaxLines.ONE" />

    <p-space :mobile="PSpaceSize.GROW" />
    <p-space :mobile="PSpaceSize.XS" />
    <p-card
      v-if="modelValue"
      :color="Color.POSITIVE_TRANSPARENT"
      class="ds-select-card__active-indicator p-flex p-items-center p-justify-center"
      :round="PCardRound.MD"
    >
      <p-icon icon="check" :size="12" :color="Color.POSITIVE_DARK" />
    </p-card>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';
@import '../../assets/scss/shared/variables/ds.radius';

.ds-select-card {
  min-width: 200px;
  height: 40px;
  padding: 0 12px;
  background-color: getColor('white');
  border-radius: $radius-md-mobile;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  border: 1px solid getColor('grey200');

  @include mobile {
    width: 100%;
    height: 48px;
  }

  &:hover:not(.ds-select-card__disable) {
    box-shadow: 0 0 10px 0 getColor('grey100');
  }

  &__gray {
    background-color: getColor('grey100');
  }

  &__active {
    background-color: getColor('white');
    border-color: getColor('grey200');

    &.ds-select-card__gray {
      background-color: getColor('grey100');
    }
  }

  &__disable {
    opacity: 0.5;
    pointer-events: none;
  }

  &__active-indicator {
    width: 36px;
    height: 36px;
    margin-right: -11px;

    @include mobile {
      width: 44px;
      height: 44px;
    }
  }
}
</style>
